const STATUS_MESSAGES = {
	400: 'The request was invalid. Please check your input and try again.',
	401: 'Your session has expired. Please log in again.',
	403: "You don't have permission to perform this action.",
	404: 'The requested resource was not found.',
	409: 'This conflicts with an existing resource.',
	413: 'The file is too large to upload.',
	429: 'Too many requests. Please wait a moment and try again.',
	500: 'Something went wrong on our end. Please try again later.',
	502: 'The server is temporarily unavailable. Please try again later.',
	503: 'The service is temporarily unavailable. Please try again later.',
};

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

const extractBackendMessage = data => {
	if (!data) return null;
	if (typeof data === 'string' && data.trim().length > 0) return data.trim();

	const raw = data.detail ?? data.message;

	if (typeof raw === 'string' && raw.length > 0) return raw;

	// Handle FastAPI-style validation errors: { detail: [{ msg: "..." }] }
	if (Array.isArray(raw) && raw.length > 0) {
		const messages = raw.map(e => (typeof e === 'string' ? e : e?.msg)).filter(Boolean);
		if (messages.length > 0) return messages.join('. ');
	}

	return null;
};

/**
 * Converts an API error into a user-friendly message.
 *
 * @param {Error} error - Axios error or generic Error
 * @param {Record<number, string>} [overrides] - Status-code-specific message overrides
 * @returns {string} User-friendly error message
 */
export const getUserFriendlyError = (error, overrides = {}) => {
	if (!error) return DEFAULT_MESSAGE;

	// Timeout
	if (error.code === 'ECONNABORTED') {
		return 'The request timed out. Please try again.';
	}

	// Network error — request made but no response received
	if (!error.response) {
		if (error.request) {
			return 'Unable to connect. Please check your internet connection.';
		}
		return DEFAULT_MESSAGE;
	}

	const { status, data } = error.response;

	// Caller-specific override for this status code
	if (status && overrides[status]) {
		return overrides[status];
	}

	// Backend provided a human-readable message (only for client errors)
	const backendMessage = extractBackendMessage(data);
	if (backendMessage && status >= 400 && status < 500) return backendMessage;

	// Generic status code mapping
	if (status && STATUS_MESSAGES[status]) {
		return STATUS_MESSAGES[status];
	}

	return DEFAULT_MESSAGE;
};
